function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

export function SkeletonBlock({ className = "" }) {
  return (
    <span
      className={cx("block animate-pulse rounded-md bg-zinc-800/80", className)}
      aria-hidden="true"
    />
  );
}

function SkeletonVotePanel({ ariaLabel } = {}) {
  return (
    <section
      className="min-h-[28rem] space-y-4"
      data-cls-measure="market-card-vote-panel"
      aria-label={ariaLabel}
    >
      <div className="space-y-4" aria-hidden="true">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <SkeletonBlock className="h-4 w-24" />
          <SkeletonBlock className="h-8 w-32" />
        </div>
        <div className="grid gap-2">
          <SkeletonBlock className="h-12 w-full rounded-xl" />
          <SkeletonBlock className="h-12 w-full rounded-xl" />
        </div>
        <SkeletonBlock className="h-14 w-full rounded-xl" />
        <div className="border-t border-zinc-800 pt-3">
          <section className="space-y-3 rounded-2xl border border-zinc-800/80 bg-zinc-950/35 p-3">
            <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <SkeletonBlock className="h-4 w-28" />
                <SkeletonBlock className="h-3 w-64 max-w-full" />
              </div>
              <div className="space-y-2 md:flex md:flex-col md:items-end">
                <SkeletonBlock className="h-3 w-28" />
                <SkeletonBlock className="h-3 w-20" />
              </div>
            </div>
            <div className="h-56 space-y-3 rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-3">
              <SkeletonBlock className="h-10 w-full rounded-lg" />
              <SkeletonBlock className="h-10 w-5/6 rounded-lg" />
              <SkeletonBlock className="h-10 w-2/3 rounded-lg" />
            </div>
            <div className="border-t border-zinc-800/80 pt-3">
              <div className="flex gap-2">
                <SkeletonBlock className="h-8 w-20 rounded-md" />
                <SkeletonBlock className="h-8 w-20 rounded-md" />
                <SkeletonBlock className="h-8 w-20 rounded-md" />
              </div>
              <div className="mt-3 space-y-2">
                <SkeletonBlock className="h-3 w-24" />
                <SkeletonBlock className="h-2 w-full rounded-full" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}

export function MarketCardSkeleton({ className = "" }) {
  return (
    <article
      className={cx("route-shell flex min-h-[44rem] min-w-0 w-full flex-col gap-4 md:min-h-[39rem]", className)}
      data-cls-measure="market-card"
      aria-label="Loading market"
    >
      <header className="space-y-3" aria-hidden="true">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 items-start gap-2">
            <SkeletonBlock className="h-10 w-10 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonBlock className="h-5 w-64 max-w-full" />
              <SkeletonBlock className="h-4 w-40 max-w-full md:hidden" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            <SkeletonBlock className="h-7 w-28 rounded-full" />
            <SkeletonBlock className="h-8 w-20 rounded-md" />
          </div>
        </div>
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-4/5" />
        <div className="flex gap-2">
          <SkeletonBlock className="h-8 w-20 rounded-md" />
          <SkeletonBlock className="h-8 w-24 rounded-md" />
        </div>
      </header>
      <SkeletonVotePanel />
      <footer
        className="flex flex-col gap-1 border-t border-zinc-800 pt-3 md:flex-row md:items-center md:justify-between"
        aria-hidden="true"
      >
        <SkeletonBlock className="h-3 w-40" />
        <SkeletonBlock className="h-3 w-20" />
      </footer>
    </article>
  );
}

export function CompactMarketCardSkeleton() {
  return (
    <article className="min-h-[12rem] rounded-xl border border-zinc-800 bg-zinc-900/70 p-3" aria-label="Loading market">
      <div className="space-y-3" aria-hidden="true">
        <div className="flex items-start gap-2">
          <SkeletonBlock className="h-8 w-8 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-48 max-w-full" />
            <SkeletonBlock className="h-3 w-28" />
          </div>
        </div>
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-4/5" />
        <div className="grid gap-2">
          <SkeletonBlock className="h-9 w-full rounded-lg" />
          <SkeletonBlock className="h-9 w-full rounded-lg" />
        </div>
      </div>
    </article>
  );
}

export function MarketDetailSkeleton() {
  return (
    <article
      className="route-shell min-h-[92.5rem] space-y-4 md:min-h-[69rem]"
      data-cls-measure="market-detail"
      aria-label="Loading market"
    >
      <header className="space-y-3" aria-hidden="true">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <SkeletonBlock className="h-10 w-10 shrink-0 rounded-full" />
            <SkeletonBlock className="h-8 w-72 max-w-full" />
          </div>
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            <SkeletonBlock className="h-7 w-32 rounded-full" />
            <SkeletonBlock className="h-8 w-24 rounded-md" />
          </div>
        </div>
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-4/5" />
        <div className="flex gap-2">
          <SkeletonBlock className="h-8 w-20 rounded-md" />
          <SkeletonBlock className="h-8 w-24 rounded-md" />
        </div>
        <SkeletonBlock className="h-3 w-80 max-w-full" />
      </header>
      <SkeletonVotePanel />
      <MarketSocialProofSkeleton />
    </article>
  );
}

export function UpcomingMarketRowSkeleton({ label = "Loading upcoming market" } = {}) {
  return (
    <div
      className="min-h-[9rem] rounded-xl border border-zinc-800 bg-zinc-900/50 p-3"
      data-cls-measure="upcoming-market-row"
      aria-label={label}
    >
      <div className="space-y-3" aria-hidden="true">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 items-start gap-2">
            <SkeletonBlock className="h-8 w-8 shrink-0 rounded-full" />
            <SkeletonBlock className="h-5 w-64 max-w-full" />
          </div>
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-8 w-20 rounded-md" />
          </div>
        </div>
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-4/5" />
        <SkeletonBlock className="h-3 w-56 max-w-full" />
      </div>
    </div>
  );
}

export function LeaderboardPanelSkeleton({ className = "" }) {
  return (
    <aside
      className={cx("route-shell min-h-[32rem] min-w-0 space-y-4 lg:min-h-[39rem]", className)}
      data-cls-measure="leaderboard-panel"
      aria-label="Loading leaderboard"
    >
      <div className="space-y-2" aria-hidden="true">
        <SkeletonBlock className="h-3 w-36" />
        <SkeletonBlock className="h-5 w-40" />
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-4/5" />
      </div>
      <div className="space-y-4" aria-hidden="true">
        {[0, 1, 2, 3].map((section) => (
          <section key={section} className={section === 0 ? "space-y-2" : "space-y-2 border-t border-zinc-800 pt-3"}>
            <SkeletonBlock className="h-4 w-44" />
            <SkeletonBlock className="h-4 w-full" />
            <SkeletonBlock className="h-4 w-5/6" />
            <SkeletonBlock className="h-4 w-3/4" />
          </section>
        ))}
      </div>
    </aside>
  );
}

export function LeaderboardGroupSkeleton({ titleRows = 3 }) {
  return (
    <article
      className="route-shell min-h-[24rem] space-y-4"
      data-cls-measure="leaderboard-group"
      aria-label="Loading leaderboard group"
    >
      <div className="space-y-2" aria-hidden="true">
        <SkeletonBlock className="h-3 w-28" />
        <SkeletonBlock className="h-5 w-44" />
        <SkeletonBlock className="h-4 w-full max-w-2xl" />
        <SkeletonBlock className="h-4 w-4/5 max-w-xl" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3" aria-hidden="true">
        {Array.from({ length: titleRows }, (_, index) => (
          <section key={index} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4">
            <SkeletonBlock className="mb-3 h-4 w-36" />
            <SkeletonBlock className="mb-2 h-4 w-full" />
            <SkeletonBlock className="mb-2 h-4 w-5/6" />
            <SkeletonBlock className="h-4 w-3/4" />
          </section>
        ))}
      </div>
    </article>
  );
}

function boundedSkeletonCount(value, fallback = 4) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(6, Math.max(1, Math.floor(numeric)));
}

export function MarketSocialProofSkeleton({ optionCount = 4 } = {}) {
  const optionSkeletonCount = boundedSkeletonCount(optionCount);

  return (
    <section
      className="min-h-[34rem] space-y-4 rounded-2xl border border-zinc-800/80 bg-zinc-950/35 p-3 md:min-h-[24rem]"
      data-cls-measure="market-social-proof"
      aria-label="Loading market social proof"
    >
      <div className="space-y-2" aria-hidden="true">
        <SkeletonBlock className="h-4 w-36" />
        <SkeletonBlock className="h-3 w-72 max-w-full" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)]" aria-hidden="true">
        <div className="space-y-3" data-cls-measure="market-social-proof-top-voters">
          <SkeletonBlock className="h-4 w-28" />
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: optionSkeletonCount }, (_, index) => (
              <div
                key={index}
                className="min-h-[8rem] rounded-xl border border-zinc-800 bg-zinc-950/50 p-3"
                data-skeleton="market-social-proof-option"
              >
                <SkeletonBlock className="mb-3 h-4 w-24" />
                <SkeletonBlock className="mb-2 h-4 w-full" />
                <SkeletonBlock className="mb-2 h-4 w-5/6" />
                <SkeletonBlock className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3" data-cls-measure="market-social-proof-activity">
          <SkeletonBlock className="h-4 w-32" />
          <div className="min-h-[18rem] rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
            {[0, 1, 2, 3, 4].map((index) => (
              <SkeletonBlock key={index} className="mb-3 h-4 w-full" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function MarketCommentsSkeleton() {
  return (
    <section
      className="min-h-[32rem] space-y-4 rounded-2xl border border-zinc-800/80 bg-zinc-950/35 p-3"
      data-cls-measure="market-comments"
      aria-label="Loading market comments"
    >
      <div className="space-y-2" aria-hidden="true">
        <SkeletonBlock className="h-4 w-32" />
        <SkeletonBlock className="h-3 w-96 max-w-full" />
      </div>
      <div
        className="min-h-[9rem] space-y-3 rounded-xl border border-zinc-800 bg-zinc-950/50 p-3"
        data-cls-measure="market-comments-composer"
        aria-hidden="true"
      >
        <SkeletonBlock className="h-4 w-44" />
        <SkeletonBlock className="h-16 w-full rounded-lg" />
        <div className="flex items-center justify-between gap-3">
          <SkeletonBlock className="h-3 w-28" />
          <SkeletonBlock className="h-8 w-28 rounded-md" />
        </div>
      </div>
      <div
        className="min-h-[17rem] space-y-3"
        data-cls-measure="market-comments-list"
        aria-hidden="true"
      >
        {[0, 1, 2].map((index) => (
          <div key={index} className="min-h-[5rem] rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
            <SkeletonBlock className="mb-3 h-4 w-40" />
            <SkeletonBlock className="mb-2 h-4 w-full" />
            <SkeletonBlock className="h-4 w-5/6" />
          </div>
        ))}
      </div>
    </section>
  );
}

export function RouteSkeleton({ variant = "default" }) {
  const body =
    variant === "leaderboards" ? (
      <>
        <LeaderboardGroupSkeleton titleRows={1} />
        <LeaderboardGroupSkeleton />
        <LeaderboardGroupSkeleton />
      </>
    ) : (
      <>
        <article className="route-shell min-h-[12rem] space-y-3">
          <SkeletonBlock className="h-5 w-48" />
          <SkeletonBlock className="h-4 w-full max-w-2xl" />
          <SkeletonBlock className="h-4 w-4/5 max-w-xl" />
        </article>
        <article className="route-shell min-h-[12rem] space-y-3">
          <SkeletonBlock className="h-5 w-40" />
          <SkeletonBlock className="h-4 w-full max-w-2xl" />
          <SkeletonBlock className="h-4 w-3/4 max-w-xl" />
        </article>
      </>
    );

  return (
    <section className="min-h-[85rem] space-y-4 md:min-h-[62rem]" aria-label="Loading route">
      <div className="editorial-header min-h-[12rem]" data-cls-measure="route-header">
        <div className="space-y-3" aria-hidden="true">
          <SkeletonBlock className="h-3 w-36" />
          <SkeletonBlock className="h-8 w-72 max-w-full md:h-12 md:w-[32rem]" />
          <SkeletonBlock className="h-4 w-full max-w-2xl" />
          <SkeletonBlock className="h-4 w-4/5 max-w-xl" />
        </div>
        <SkeletonBlock className="h-11 w-36 rounded-lg" />
      </div>
      {body}
    </section>
  );
}

export function MarketCardVotePanelSkeleton() {
  return <SkeletonVotePanel ariaLabel="Loading market vote panel" />;
}
